import { useEffect, useRef, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';

import { ROUTES, isScopedRoute } from '@/shared/constants/routes';
import { useAppSelector } from '@/shared/hooks/useStore';
import { selectCurrentProject } from '@/viewmodel/features/project';
import { useApplyContextScope } from '@/viewmodel/features/project';
import { useWorkspaceQuery } from '@/viewmodel/features/workspace';
import { selectCurrentWorkspace } from '@/viewmodel/features/workspace';

export function useScopeFromUrl(): void {
  const [params, setParams] = useSearchParams();
  const location = useLocation();
  const { pathname } = location;
  const w = params.get('w');
  const p = params.get('p');
  const currentWorkspace = useAppSelector(selectCurrentWorkspace);
  const currentProject = useAppSelector(selectCurrentProject);
  const { workspaces, isSettled: workspacesSettled } = useWorkspaceQuery();
  const applyContextScope = useApplyContextScope();
  const syncedUrlKeyRef = useRef<string | null>(null);
  const isMountedRef = useRef(true);
  const resolvingKeyRef = useRef<string | null>(null);
  const [, forceRerenderOnSettle] = useState(0);

  const urlKey = w ? `${w}|${p ?? ''}` : null;
  const urlPending = urlKey !== null && syncedUrlKeyRef.current !== urlKey;
  const currentProjectHasRequestId = Boolean(currentProject?.request_id);

  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!w) {
      syncedUrlKeyRef.current = null;
      return;
    }

    const key = `${w}|${p ?? ''}`;
    if (syncedUrlKeyRef.current === key || resolvingKeyRef.current === key) return;

    if (
      w === currentWorkspace?.id &&
      (p ?? undefined) === (currentProject?.id ?? undefined) &&
      (!p || currentProjectHasRequestId)
    ) {
      syncedUrlKeyRef.current = key;
      forceRerenderOnSettle((n) => n + 1);
      return;
    }

    const workspace = workspaces.find((ws) => ws.id === w);
    if (!workspace) {
      if (workspacesSettled) {
        syncedUrlKeyRef.current = key;
        forceRerenderOnSettle((n) => n + 1);
      }
      return;
    }

    resolvingKeyRef.current = key;
    void applyContextScope({ workspaceId: w, ...(p ? { projectId: p } : {}) }).finally(() => {
      if (resolvingKeyRef.current !== key) return;
      if (!isMountedRef.current) return;
      syncedUrlKeyRef.current = key;
      resolvingKeyRef.current = null;
      forceRerenderOnSettle((n) => n + 1);
    });
  }, [
    w,
    p,
    currentWorkspace?.id,
    currentProject?.id,
    currentProjectHasRequestId,
    workspaces,
    workspacesSettled,
    applyContextScope,
  ]);

  useEffect(() => {
    if (urlPending) return;

    const workspaceId = currentWorkspace?.id;
    if (!workspaceId || !isScopedRoute(pathname)) return;

    const wantProject = pathname === ROUTES.PROJECTS ? null : (currentProject?.id ?? null);
    if (w === workspaceId && (p ?? null) === wantProject) return;

    const next = new URLSearchParams(params);
    next.set('w', workspaceId);
    if (wantProject) next.set('p', wantProject);
    else next.delete('p');
    setParams(next, { replace: true, state: location.state });
  }, [
    urlPending,
    pathname,
    location.state,
    currentWorkspace?.id,
    currentProject?.id,
    w,
    p,
    params,
    setParams,
  ]);
}
